import React, { useState, useEffect, useRef } from 'react';

/**
 * Animated number that counts up from previous value to target.
 * Handles prefix/suffix (e.g. $, %), decimals, smart formatting (K/M),
 * and an optional color pulse when the value changes.
 */
export default function CountUp({
  value = 0,
  prefix = '',
  suffix = '',
  decimals = 0,
  duration = 1000,
  compact = false,
  pulseOnChange = false,
  className,
  style,
}) {
  const [display, setDisplay] = useState(0);
  const [pulsing, setPulsing] = useState(false);
  const rafRef = useRef(null);
  const startValRef = useRef(0);
  const prevValueRef = useRef(0);

  useEffect(() => {
    const target = Number(value) || 0;
    const start = startValRef.current;
    const startTime = performance.now();

    // Trigger pulse if value changed meaningfully
    if (pulseOnChange && target !== prevValueRef.current) {
      setPulsing(true);
      const t = setTimeout(() => setPulsing(false), 600);
      prevValueRef.current = target;
      // cleanup handled below
      var pulseTimeout = t;
    }

    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutExpo — fast start, smooth settle
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      const current = start + (target - start) * eased;
      setDisplay(current);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        startValRef.current = target;
      }
    };

    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      // eslint-disable-next-line no-undef
      if (typeof pulseTimeout !== 'undefined') clearTimeout(pulseTimeout);
    };
  }, [value, duration, pulseOnChange]);

  // Compact formatting — 1.2K, 3.4M, 1.1B
  const formatCompact = (num) => {
    const abs = Math.abs(num);
    if (abs >= 1e9) return (num / 1e9).toFixed(1) + 'B';
    if (abs >= 1e6) return (num / 1e6).toFixed(1) + 'M';
    if (abs >= 1e3) return (num / 1e3).toFixed(1) + 'K';
    return num.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const formatted = compact
    ? formatCompact(display)
    : display.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });

  return (
    <span
      className={`${className || ''} ${pulsing ? 'count-pulse' : ''}`}
      style={{
        transition: 'color 0.3s ease, transform 0.3s ease',
        ...style,
      }}
    >
      {prefix}{formatted}{suffix}
    </span>
  );
}
